import React, { useState, useEffect, useCallback, useRef } from 'react';
import SyringeWarningDialog from '@/components/clinic/PrescriptionFormSheet/SyringeWarningDialog';
import { checkSyringeNeeded } from '@/components/clinic/PrescriptionFormSheet/syringeCheck';
import AllergyWarningDialog, { type AllergyWarning } from '@/components/clinic/PrescriptionFormSheet/AllergyWarningDialog';
import { checkAllergyInteractions } from '@/services/products.service';
import {
  Sheet,
  SheetContent,
  SheetTitle,
  SheetClose,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  UserPlus,
  Package,
  CreditCard,
  Calendar,
  Loader2,
  AlertCircle,
  Check,
  ChevronRight,
  ChevronLeft,
  Pill,
  X,
  Lock,
  ShieldCheck,
  RefreshCw,
  ChevronsUpDown,
  User,
  Search,
  AlertTriangle,
  Plus,
  Minus,
} from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { toast } from '@/hooks/use-toast';
import { useClinic } from '@/contexts/ClinicContext';
import { patientService } from '@/services/patientService';
import { practitionerService } from '@/services/practitionerService';
import { getPrograms, getProgramDetails, Program, Medication } from '@/services/storefrontService';
import subscriptionService from '@/services/subscriptionService';
import {
  shippingService,
  enrollMedicationsToShippingLines,
  type ShippingCarrier,
  type ShippingOption,
  type ShippingSelection,
} from '@/services/shippingService';
import {
  processPayment,
  isStripeOnboardingRequiredError,
  STRIPE_ONBOARDING_TOAST,
} from '@/services/paymentService'
import { formatToLocalDate } from '@/lib/utils';
import { formatDOB } from '@/lib/dateUtils';
import { StripePaymentForm } from '@/components/payments/StripePaymentForm'
import { getStripeInstance } from '@/lib/stripe-loader'
import { useNavigate } from 'react-router-dom'
import { ToastAction } from '@/components/ui/toast'

interface Patient {
  id: string;
  first_name?: string;
  last_name?: string;
  full_name?: string;
  email: string;
  phone?: string;
  date_of_birth?: string;
  dob?: string;
  gender?: string;
  medication_allergies?: string[];
}

interface PractitionerOption {
  id: string;
  first_name?: string;
  last_name?: string;
  full_name?: string;
  email?: string;
}

interface MedicationSelection {
  product_id: string;
  medication: string;
  dose: string;
  quantity: number;
  price: number;
  sig?: string;
  daysSupply?: string;
  template_id?: string;
  product_type?: string | null;
}

interface EnrollPatientDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  practitionerId: string;
  onSuccess?: () => void;
}


// Base steps — practitioner step injected dynamically for non-practitioner users
const BASE_STEPS = [
  { id: 'patient', label: 'Select Patient' },
  { id: 'practitioner', label: 'Select Practitioner' },
  { id: 'program', label: 'Choose Program' },
  { id: 'medications', label: 'Configure Medications' },
  { id: 'payment', label: 'Payment & Review' },
];

// Pastel color palette for program cards (matching Storefront)
const colorPalette = [
  { bg: 'bg-[#94D2BD]', light: 'bg-[#94D2BD]/10', text: 'text-[#2D6A5D]', accent: 'from-[#94D2BD]/10' },
  { bg: 'bg-[#B8B8FF]', light: 'bg-[#B8B8FF]/10', text: 'text-[#5D5DA8]', accent: 'from-[#B8B8FF]/10' },
  { bg: 'bg-[#FFD6A5]', light: 'bg-[#FFD6A5]/10', text: 'text-[#9A7B4F]', accent: 'from-[#FFD6A5]/10' },
  { bg: 'bg-[#FFADAD]', light: 'bg-[#FFADAD]/10', text: 'text-[#A65D5D]', accent: 'from-[#FFADAD]/10' },
  { bg: 'bg-[#A0C4FF]', light: 'bg-[#A0C4FF]/10', text: 'text-[#5075A8]', accent: 'from-[#A0C4FF]/10' },
  { bg: 'bg-[#CAFFBF]', light: 'bg-[#CAFFBF]/10', text: 'text-[#5A8A4F]', accent: 'from-[#CAFFBF]/10' },
  { bg: 'bg-[#A99BC9]', light: 'bg-[#A99BC9]/10', text: 'text-[#6B5B8A]', accent: 'from-[#A99BC9]/10' },
  { bg: 'bg-[#FFC6FF]', light: 'bg-[#FFC6FF]/10', text: 'text-[#8A5D8A]', accent: 'from-[#FFC6FF]/10' },
  { bg: 'bg-[#9BF6FF]', light: 'bg-[#9BF6FF]/10', text: 'text-[#4A8A8A]', accent: 'from-[#9BF6FF]/10' },
  { bg: 'bg-[#E2CFC4]', light: 'bg-[#E2CFC4]/10', text: 'text-[#7A6A5F]', accent: 'from-[#E2CFC4]/10' },
];

const getColorForIndex = (index: number) => colorPalette[index % colorPalette.length];
const getInitials = (title: string) => title.charAt(0).toUpperCase();

const EnrollPatientDialog: React.FC<EnrollPatientDialogProps> = ({
  open,
  onOpenChange,
  practitionerId,
  onSuccess,
}) => {
  const { clinic, isPractitioner, isAdmin } = useClinic();
  const navigate = useNavigate();
  const stripeAccountId = clinic?.paymentAccount?.stripe_account_id
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Step 1: Patient selection
  const [patientSearch, setPatientSearch] = useState('');
  const [patients, setPatients] = useState<Patient[]>([]);
  const [searchingPatients, setSearchingPatients] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [patientDropdownOpen, setPatientDropdownOpen] = useState(false);

  // Step 1b: Practitioner selection (only for non-practitioner users)
  const isPractitionerUser = !!practitionerId;
  const STEPS = isPractitionerUser
    ? BASE_STEPS.filter(s => s.id !== 'practitioner')
    : BASE_STEPS;
  const [practitioners, setPractitioners] = useState<PractitionerOption[]>([]);
  const [loadingPractitioners, setLoadingPractitioners] = useState(false);
  const [selectedPractitioner, setSelectedPractitioner] = useState<PractitionerOption | null>(null);
  const [practitionerDropdownOpen, setPractitionerDropdownOpen] = useState(false);
  const [practitionerSearch, setPractitionerSearch] = useState('');

  // Step 2: Program selection
  const [programs, setPrograms] = useState<Program[]>([]);
  const [loadingPrograms, setLoadingPrograms] = useState(false);
  const [selectedProgram, setSelectedProgram] = useState<Program | null>(null);
  const [programDetails, setProgramDetails] = useState<any>(null);
  const [allMedications, setAllMedications] = useState<Medication[]>([]);

  // Step 3: Medication configuration
  const [medicationSearch, setMedicationSearch] = useState('');
  const [selectedMedications, setSelectedMedications] = useState<MedicationSelection[]>([]);
  const [frequency, setFrequency] = useState<'weekly' | 'monthly' | 'quarterly'>('monthly');
  const [autoRenew, setAutoRenew] = useState(true);

  // Syringe warning state
  const [showSyringeWarning, setShowSyringeWarning] = useState(false);
  const [syringeWarningMedications, setSyringeWarningMedications] = useState<string[]>([]);
  const [syringeWarningAcknowledged, setSyringeWarningAcknowledged] = useState(false);

  // Allergy warning state
  const [showAllergyWarning, setShowAllergyWarning] = useState(false);
  const [allergyWarnings, setAllergyWarnings] = useState<AllergyWarning[]>([]);
  const [allergyWarningsAcknowledged, setAllergyWarningsAcknowledged] = useState(false);

  // Step 4: Payment
  const [paymentError, setPaymentError] = useState('');
  const [carriers, setCarriers] = useState<ShippingCarrier[]>([]);
  const [selectedCarrierId, setSelectedCarrierId] = useState('');
  const [shippingOptions, setShippingOptions] = useState<ShippingOption[]>([]);
  const [selectedShippingOptionId, setSelectedShippingOptionId] = useState('');
  const [isLoadingCarriers, setIsLoadingCarriers] = useState(false);
  const [isLoadingShippingOptions, setIsLoadingShippingOptions] = useState(false);
  const [shippingError, setShippingError] = useState<string | null>(null);
  const [isFreeShipment, setIsFreeShipment] = useState(false);
  const [freeShipmentType, setFreeShipmentType] = useState<string | null>(null);
  const [patientState, setPatientState] = useState<string | null>(null);
  const [shippingCostDollars, setShippingCostDollars] = useState(0);
  const [shippingOriginalCostDollars, setShippingOriginalCostDollars] = useState(0);

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setCurrentStep(0);
      setSelectedPatient(null);
      setSelectedProgram(null);
      setProgramDetails(null);
      setSelectedMedications([]);
      setFrequency('monthly');
      setAutoRenew(true);
      setPaymentError('');
      setPatientSearch('');
      setPatients([]);
      setAllMedications([]);
      setMedicationSearch('');
      setCarriers([]);
      setSelectedCarrierId('');
      setShippingOptions([]);
      setSelectedShippingOptionId('');
      setIsLoadingCarriers(false);
      setIsLoadingShippingOptions(false);
      setShippingError(null);
      setIsFreeShipment(false);
      setFreeShipmentType(null);
      setPatientState(null);
      setShippingCostDollars(0);
      setShippingOriginalCostDollars(0);
      setSelectedPractitioner(null);
      setPractitionerSearch('');
      setPractitioners([]);
      setSyringeWarningAcknowledged(false);
      setSyringeWarningMedications([]);
      setShowSyringeWarning(false);
      setAllergyWarningsAcknowledged(false);
      setAllergyWarnings([]);
      setShowAllergyWarning(false);
    }
  }, [open]);

  // Search patients (empty search returns all patients)
  const searchPatients = useCallback(async () => {
    if (!clinic?.id) return;

    setSearchingPatients(true);
    try {
      const response = await patientService.getPatientsForDropdown(patientSearch, clinic.id);
      setPatients(response.data.patients || []);
    } catch (error) {
      console.error('Error searching patients:', error);
      toast({
        title: 'Error',
        description: 'Failed to search patients',
        variant: 'destructive',
      });
    } finally {
      setSearchingPatients(false);
    }
  }, [patientSearch, clinic?.id]);

  // Debounced search (loads all patients when empty)
  useEffect(() => {
    const timer = setTimeout(() => {
      searchPatients();
    }, 300);
    return () => clearTimeout(timer);
  }, [patientSearch, searchPatients]);

  // Load practitioners for non-practitioner users
  const loadPractitioners = useCallback(async () => {
    if (!clinic?.id || isPractitionerUser) return;
    setLoadingPractitioners(true);
    try {
      const response = await practitionerService.getPractitionerDropdownByClinic(clinic.id, {
        limit: 100,
        search: practitionerSearch || undefined,
      });
      const list = response.data?.practitioners || response.data?.data || [];
      setPractitioners(list);
    } catch (error) {
      console.error('Error loading practitioners:', error);
      toast({ title: 'Error', description: 'Failed to load practitioners', variant: 'destructive' });
    } finally {
      setLoadingPractitioners(false);
    }
  }, [clinic?.id, isPractitionerUser, practitionerSearch]);

  useEffect(() => {
    if (!isPractitionerUser && open) {
      const timer = setTimeout(() => loadPractitioners(), 300);
      return () => clearTimeout(timer);
    }
  }, [isPractitionerUser, open, practitionerSearch, loadPractitioners]);

  // Load programs when program step becomes active
  useEffect(() => {
    if (STEPS[currentStep]?.id === 'program' && programs.length === 0 && clinic?.id) {
      loadPrograms();
    }
  }, [currentStep, clinic?.id]);

  const loadPrograms = async () => {
    if (!clinic?.id) return;

    setLoadingPrograms(true);
    try {
      const response = await getPrograms({ clinicId: clinic?.id, limit: 50 });
      const directPrograms = (response.programs || []).filter(
        p => p.display_mode === 'medication_select'
      );
      setPrograms(directPrograms);
    } catch (error) {
      console.error('Error loading programs:', error);
      toast({
        title: 'Error',
        description: 'Failed to load programs',
        variant: 'destructive',
      });
    } finally {
      setLoadingPrograms(false);
    }
  };

  // Load program details when program is selected (for program metadata)
  useEffect(() => {
    if (selectedProgram && clinic?.id) {
      loadProgramDetails(selectedProgram.id);
    }
  }, [selectedProgram, clinic?.id, selectedPatient?.id]);

  const loadProgramDetails = async (programId: string) => {
    try {
      const details = await getProgramDetails(programId, clinic?.id, undefined, selectedPatient?.id);
      setProgramDetails(details);
    } catch (error) {
      console.error('Error loading program details:', error);
    }
  };

  // Load medications for the selected program when entering medications step
  useEffect(() => {
    if (STEPS[currentStep]?.id === 'medications' && selectedProgram && clinic?.id) {
      loadSelectedProgramMedications();
    }
  }, [currentStep, selectedProgram, clinic?.id, selectedPatient?.id]);

  const loadSelectedProgramMedications = async () => {
    if (!selectedProgram) return;
    setLoading(true);
    try {
      const details = await getProgramDetails(selectedProgram.id, clinic?.id, undefined, selectedPatient?.id);
      setAllMedications(details?.medications || []);
    } catch (error) {
      console.error('Error loading medications:', error);
      toast({
        title: 'Error',
        description: 'Failed to load medications',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  // Calculate totals
  const medicationTotalAmount = selectedMedications.reduce(
    (sum, med) => sum + (med.price * med.quantity),
    0,
  );
  const totalAmount = medicationTotalAmount + shippingCostDollars;

  const normalizedMedicationSearch = medicationSearch.trim().toLowerCase();
  const filteredMedicationRows = allMedications?.flatMap((medication: Medication) =>
    medication.map_dose_to_product
      ?.filter(doseInfo => {
        if (!normalizedMedicationSearch) return true;
        const searchableText = `${medication.name || ''} ${doseInfo.dose || ''}`.toLowerCase();
        return searchableText.includes(normalizedMedicationSearch);
      })
      ?.map(doseInfo => ({ medication, doseInfo })) || []
  );

  // Add medication to selection (quantity 1, or increment if already added)
  const addMedication = (medication: Medication, doseInfo: any) => {
    const existing = selectedMedications.find(
      m => m.product_id === doseInfo.product_id && m.dose === doseInfo.dose
    );

    if (existing) {
      updateQuantity(doseInfo.product_id, doseInfo.dose, existing.quantity + 1);
      return;
    }
    setSelectedMedications(prev => [
      ...prev,
      {
        product_id: doseInfo.product_id,
        medication: medication.name,
        dose: doseInfo.dose,
        quantity: 1,
        price: parseFloat(doseInfo.price) || 0,
        sig: medication.sig?.[0] || 'Take as directed',
        daysSupply: '30',
        template_id: (medication as any).template_id || medication.pharmetika_id || (medication as any).pmk_template_id,
        product_type: doseInfo.product_type || (medication as any).product_type || null,
      },
    ]);
  };

  // Update quantity for a selected medication
  const updateQuantity = (productId: string, dose: string, newQuantity: number) => {
    const qty = Math.max(1, Math.min(99, newQuantity));
    setSelectedMedications(prev =>
      prev.map(m =>
        m.product_id === productId && m.dose === dose ? { ...m, quantity: qty } : m
      )
    );
  };

  // Remove medication from selection
  const removeMedication = (productId: string, dose: string) => {
    setSelectedMedications(prev =>
      prev.filter(m => !(m.product_id === productId && m.dose === dose))
    );
  };

  // Handle payment method from Stripe form
  const handleStripePaymentMethodReceived = (paymentMethodId: string) => {
    setPaymentError('')
    processEnrollment('', '', paymentMethodId)
  }

  const getShippingSelection = (): ShippingSelection | null => {
    if (!selectedCarrierId || !selectedShippingOptionId) return null;

    const carrier = carriers.find(c => c.id === selectedCarrierId);
    const option = shippingOptions.find(o => o.id === selectedShippingOptionId);
    if (!carrier || !option) return null;

    return {
      carrierId: carrier.id,
      carrierName: carrier.name,
      optionId: option.id,
      optionName: option.name,
      cost: option.totalCost,
      originalCost: option.originalCost,
      isFreeShipment,
      freeShipmentType,
    };
  };

  const fetchCarriers = useCallback(async () => {
    if (!clinic?.id) return;
    setIsLoadingCarriers(true);
    try {
      const result = await shippingService.getCarriers(clinic.id);
      if (result.success && result.data) {
        setCarriers(result.data);
      } else {
        setShippingError(result.error || 'Failed to load shipping carriers');
      }
    } catch (error) {
      setShippingError('Failed to load shipping carriers');
    } finally {
      setIsLoadingCarriers(false);
    }
  }, [clinic?.id]);

  const handleShippingOptionChange = useCallback(
    (optionId: string, options?: ShippingOption[]) => {
      setSelectedShippingOptionId(optionId);
      const optionsToSearch = options || shippingOptions;
      const option = optionsToSearch.find(o => o.id === optionId);
      if (!option) {
        setShippingCostDollars(0);
        setShippingOriginalCostDollars(0);
        return;
      }
      setShippingCostDollars(option.totalCost);
      setShippingOriginalCostDollars(option.originalCost);
    },
    [shippingOptions],
  );

  const handleCarrierChange = useCallback(
    async (carrierId: string) => {
      setSelectedCarrierId(carrierId);
      setSelectedShippingOptionId('');
      setShippingOptions([]);
      setShippingCostDollars(0);
      setShippingOriginalCostDollars(0);
      setShippingError(null);
      setIsFreeShipment(false);
      setFreeShipmentType(null);
      setPatientState(null);

      if (!carrierId || !clinic?.id || !selectedPatient?.id) return;

      setIsLoadingShippingOptions(true);
      try {
        const result = await shippingService.calculateShippingCost(
          clinic.id,
          selectedPatient.id,
          carrierId,
          enrollMedicationsToShippingLines(selectedMedications),
        );
        if (result.success && result.data) {
          setShippingOptions(result.data);
          setIsFreeShipment(result.isFreeShipment);
          setFreeShipmentType(result.freeShipmentType || null);
          setPatientState(result.patientState || null);
          setShippingError(null);
          if (result.data.length > 0) {
            handleShippingOptionChange(result.data[0].id, result.data);
          }
        } else {
          setShippingError(
            result.error || 'Failed to calculate shipping cost. Please try a different carrier.',
          );
        }
      } catch (error) {
        setShippingError('Failed to load shipment types. Please try again.');
      } finally {
        setIsLoadingShippingOptions(false);
      }
    },
    [clinic?.id, selectedPatient?.id, selectedMedications, handleShippingOptionChange],
  );

  useEffect(() => {
    if (open && STEPS[currentStep]?.id === 'payment' && clinic?.id && carriers.length === 0) {
      fetchCarriers();
    }
  }, [open, currentStep, clinic?.id, carriers.length, fetchCarriers]);

  useEffect(() => {
    setSelectedCarrierId('');
    setSelectedShippingOptionId('');
    setShippingOptions([]);
    setShippingCostDollars(0);
    setShippingOriginalCostDollars(0);
    setShippingError(null);
    setIsFreeShipment(false);
    setFreeShipmentType(null);
    setPatientState(null);
  }, [selectedPatient?.id]);

  // Handle payment error
  const handlePaymentError = (error: string) => {
    setPaymentError(error);
    if (isStripeOnboardingRequiredError({ message: error })) {
      toast({
        ...STRIPE_ONBOARDING_TOAST,
        variant: 'destructive',
        ...(isAdmin ? { action: (
          <ToastAction altText="Go to Payments" onClick={() => navigate('/payments?tab=settings')}>
            Go to Payments
          </ToastAction>
        ) } : {}),
      });
    } else {
      toast({
        title: 'Payment Error',
        description: error,
        variant: 'destructive',
      });
    }
  };

  const processEnrollment = async (
    token: string,
    sessionId: string,
    paymentMethodId?: string
  ) => {
    if (!selectedPatient || !clinic?.id || selectedMedications.length === 0) {
      toast({
        title: 'Validation Error',
        description: 'Please complete all required fields',
        variant: 'destructive',
      })
      return
    }

    setSubmitting(true)
    try {
      if (!selectedProgram?.id) {
        throw new Error('Please select a program for subscription enrollment')
      }

      const shippingSelection = getShippingSelection()
      if (!shippingSelection) {
        throw new Error('Please select shipping carrier and shipment type')
      }
      const normalizedFreeShipmentType: 'global' | 'clinic_specific' | null
        = shippingSelection.freeShipmentType === 'global'
          || shippingSelection.freeShipmentType === 'clinic_specific'
          ? (shippingSelection.freeShipmentType as 'global' | 'clinic_specific')
          : null

      const result = await subscriptionService.enrollPatient({
        patient_id: selectedPatient.id,
        practitioner_id: practitionerId || selectedPractitioner?.id || '',
        clinic_id: clinic.id,
        program_id: selectedProgram.id,
        medications: selectedMedications.map(med => ({
          product_id: med.product_id,
          medication: med.medication,
          dose: med.dose,
          quantity: med.quantity,
          price: med.price,
          sig: med.sig,
          daysSupply: med.daysSupply,
          template_id: med.template_id,
        })),
        frequency,
        payment_method_id: paymentMethodId,
        auto_renew: autoRenew,
        allergyWarningsAcknowledged,
        shipping: {
          carrier_id: shippingSelection.carrierId,
          carrier_name: shippingSelection.carrierName,
          service_id: shippingSelection.optionId,
          service_name: shippingSelection.optionName,
          cost_cents: Math.round(shippingSelection.cost * 100),
          original_cost_cents: Math.round(shippingSelection.originalCost * 100),
          is_free_shipment: shippingSelection.isFreeShipment,
          free_shipment_type: normalizedFreeShipmentType,
        },
      })

      const paymentError = result?.data?.payment?.error as unknown
      const requiresActionError =
        typeof paymentError === 'object' && paymentError !== null
          ? (paymentError as { requires_action?: boolean; client_secret?: string; message?: string })
          : null

      if (
        requiresActionError?.requires_action &&
        requiresActionError.client_secret &&
        paymentMethodId
      ) {
        const connectedAccountId = clinic.paymentAccount?.stripe_account_id
        if (!connectedAccountId) {
          throw new Error('Stripe account is not configured for this clinic')
        }

        const stripe = await getStripeInstance(connectedAccountId)
        if (!stripe) {
          throw new Error('Unable to initialize Stripe')
        }

        const { error } = await stripe.handleNextAction({
          clientSecret: requiresActionError.client_secret,
        })

        if (error) {
          throw new Error(error.message || 'Authentication failed')
        }

        const prescriptionId = result.data?.prescription_id
        if (!prescriptionId) {
          throw new Error('Payment authenticated but prescription ID is missing')
        }

        const recheckResult = await processPayment({
          prescription_id: prescriptionId,
          clinic_id: clinic.id,
          amount: totalAmount,
          payment_method_id: paymentMethodId,
          is_subscription: true,
          subscription_frequency: frequency,
          subscription_auto_renew: autoRenew,
          shipping: {
            carrier_id: shippingSelection.carrierId,
            carrier_name: shippingSelection.carrierName,
            service_id: shippingSelection.optionId,
            service_name: shippingSelection.optionName,
            cost_cents: Math.round(shippingSelection.cost * 100),
            original_cost_cents: Math.round(shippingSelection.originalCost * 100),
            is_free_shipment: shippingSelection.isFreeShipment,
            free_shipment_type: normalizedFreeShipmentType,
          },
        })

        if (!recheckResult.success) {
          throw new Error(recheckResult.error?.message || 'Enrollment failed')
        }
      } else if (!result.success) {
        throw new Error(result.message || 'Enrollment failed')
      }

      toast({
        title: 'Enrollment Successful',
        description: `${selectedPatient.full_name || `${selectedPatient.first_name || ''} ${selectedPatient.last_name || ''}`.trim() || selectedPatient.email} has been enrolled in the subscription.`,
        variant: 'success',
      })
      onOpenChange(false)
      onSuccess?.()
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to enroll patient in subscription'
      if (isStripeOnboardingRequiredError(error)) {
        toast({
          ...STRIPE_ONBOARDING_TOAST,
          variant: 'destructive',
          action: (
            <ToastAction altText="Go to Payments" onClick={() => navigate('/payments?tab=settings')}>
              Go to Payments
            </ToastAction>
          ),
        })
      } else {
        toast({
          title: 'Enrollment Failed',
          description: message,
          variant: 'destructive',
        })
      }
    } finally {
      setSubmitting(false)
    }
  }

  // Navigation
  const canProceed = () => {
    switch (STEPS[currentStep]?.id) {
      case 'patient':
        return !!selectedPatient;
      case 'practitioner':
        return !!selectedPractitioner;
      case 'program':
        return !!selectedProgram;
      case 'medications':
        return selectedMedications.length > 0;
      case 'payment':
        return true;
      default:
        return false;
    }
  };

  const nextStep = async () => {
    if (!canProceed() || currentStep >= STEPS.length - 1) return;

    // Allergy and syringe checks when leaving medications step
    if (STEPS[currentStep]?.id === 'medications') {
      // Allergy check (async API call)
      if (!allergyWarningsAcknowledged && selectedPatient?.id) {
        try {
          const productIds = selectedMedications.map(m => m.product_id).filter(Boolean);
          const medicationNames = selectedMedications.map(m => m.medication).filter(Boolean);
          const result = await checkAllergyInteractions(selectedPatient.id, productIds, medicationNames);
          if (result.hasWarnings) {
            setAllergyWarnings(result.warnings);
            setShowAllergyWarning(true);
            return;
          }
        } catch (err) {
          console.error('Allergy check failed, proceeding:', err);
        }
      }

      // Syringe check (synchronous)
      if (!syringeWarningAcknowledged) {
        const products = selectedMedications.map(med => ({
          product_type: med.product_type || null,
          name: med.medication || '',
        }));
        const result = checkSyringeNeeded(products);
        if (result.needsWarning) {
          setSyringeWarningMedications(result.injectableMedications);
          setShowSyringeWarning(true);
          return;
        }
      }
    }

    setCurrentStep(prev => prev + 1);
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="flex w-full !max-w-[640px] flex-col gap-0 rounded-l-2xl p-0 [&>button]:hidden">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <div>
            <SheetTitle className="font-semibold text-slate-900" style={{ fontSize: '1.05rem' }}>
              Enroll Patient in Subscription
            </SheetTitle>
            <p className="text-sm text-slate-500 mt-0.5">Create a recurring subscription for medication delivery</p>
          </div>
          <SheetClose asChild>
            <button className="flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 transition-colors hover:bg-slate-50">
              <X className="h-5 w-5 text-slate-600" />
            </button>
          </SheetClose>
        </div>

        {/* Step Progress */}
        <div className="flex items-center justify-between px-6 py-3 border-b border-slate-200" style={{ backgroundColor: 'rgb(245, 247, 247)' }}>
          {STEPS.map((step, index) => (
            <React.Fragment key={step.id}>
              <div className="flex items-center gap-2">
                <div
                  className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium ${index < currentStep
                      ? 'bg-brand-teal text-white'
                      : index === currentStep
                        ? 'bg-[#60378B] text-white'
                        : 'bg-slate-200 text-slate-500'
                    }`}
                >
                  {index < currentStep ? <Check className="h-3.5 w-3.5" /> : index + 1}
                </div>
                <span
                  className={`text-xs hidden sm:block ${index === currentStep ? 'font-medium text-slate-900' : 'text-slate-500'
                    }`}
                >
                  {step.label}
                </span>
              </div>
              {index < STEPS.length - 1 && (
                <div className="flex-1 h-px bg-slate-300 mx-2" />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {/* Step: Patient Selection */}
          {STEPS[currentStep]?.id === 'patient' && (
            <div className="space-y-4">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-[#60378B]">
                Select Patient
              </h3>

              <Popover open={patientDropdownOpen} onOpenChange={setPatientDropdownOpen} modal={true}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={patientDropdownOpen}
                    className="w-full justify-between h-11 border-slate-200 bg-white hover:bg-slate-50 font-normal"
                  >
                    {selectedPatient ? (
                      <span className="text-slate-900">
                        {selectedPatient.full_name ||
                          (selectedPatient.first_name && selectedPatient.last_name
                            ? `${selectedPatient.first_name} ${selectedPatient.last_name}`
                            : selectedPatient.email)}
                      </span>
                    ) : (
                      <span className="text-slate-500">Search and select a patient...</span>
                    )}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 text-slate-400" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                  <Command shouldFilter={false}>
                    <CommandInput
                      placeholder="Search by name or email..."
                      value={patientSearch}
                      onValueChange={(value) => setPatientSearch(value)}
                    />
                    <CommandList className="max-h-[280px]">
                      {searchingPatients && (
                        <div className="flex items-center justify-center py-6">
                          <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
                        </div>
                      )}
                      {!searchingPatients && patients.length === 0 && (
                        <CommandEmpty>No patients found.</CommandEmpty>
                      )}
                      {!searchingPatients && patients.length > 0 && (
                        <CommandGroup className="p-1">
                          {patients.map((patient) => (
                            <CommandItem
                              key={patient.id}
                              value={patient.id}
                              onSelect={() => {
                                setSelectedPatient(patient);
                                setPatientDropdownOpen(false);
                              }}
                              className="flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer data-[selected=true]:bg-slate-50"
                            >
                              <Check
                                className={`h-4 w-4 shrink-0 ${selectedPatient?.id === patient.id
                                    ? 'text-brand-teal'
                                    : 'text-transparent'
                                  }`}
                              />
                              <div className="flex items-center gap-3 flex-1 min-w-0">
                                <div className="h-10 w-10 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
                                  <User className="h-5 w-5 text-slate-500" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="font-medium text-slate-900 truncate">
                                    {patient.full_name ||
                                      (patient.first_name && patient.last_name
                                        ? `${patient.first_name} ${patient.last_name}`
                                        : patient.email || 'Unknown')}
                                  </p>
                                  <p className="text-sm text-slate-500 truncate">
                                    {[patient.email, patient.phone].filter(Boolean).join(' • ')}
                                  </p>
                                  {((patient.date_of_birth || patient.dob) || patient.gender) && (
                                    <p className="text-xs text-slate-400 mt-0.5">
                                      {[
                                        patient.date_of_birth || patient.dob
                                          ? `DOB: ${formatDOB(patient.date_of_birth || patient.dob)}`
                                          : null,
                                        patient.gender || null,
                                      ]
                                        .filter(Boolean)
                                        .join(' • ')}
                                    </p>
                                  )}
                                </div>
                              </div>
                            </CommandItem>))}
                        </CommandGroup>
                      )}
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>

              {selectedPatient && (
                <div className="flex items-center gap-3 p-4 rounded-lg bg-teal-50 border border-teal-200">
                  <div className="h-10 w-10 rounded-full bg-brand-teal/20 flex items-center justify-center shrink-0">
                    <User className="h-5 w-5 text-brand-teal" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-teal-900 truncate">
                      {selectedPatient.full_name ||
                        (selectedPatient.first_name && selectedPatient.last_name
                          ? `${selectedPatient.first_name} ${selectedPatient.last_name}`
                          : selectedPatient.email)}
                    </p>
                    <p className="text-sm text-teal-700 truncate">
                      {[selectedPatient.email, selectedPatient.phone].filter(Boolean).join(' • ')}
                    </p>
                    {selectedPatient.gender && (
                      <p className="text-xs text-teal-600 mt-0.5">{selectedPatient.gender}</p>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0 text-teal-600 hover:text-teal-800 hover:bg-teal-100 shrink-0"
                    onClick={() => setSelectedPatient(null)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Step 2 (non-practitioners): Practitioner Selection */}
          {STEPS[currentStep]?.id === 'practitioner' && (
            <div className="space-y-4">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-[#60378B]">
                Select Practitioner
              </h3>

              <Popover open={practitionerDropdownOpen} onOpenChange={setPractitionerDropdownOpen} modal={true}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={practitionerDropdownOpen}
                    className="w-full justify-between h-11 border-slate-200 bg-white hover:bg-slate-50 font-normal"
                  >
                    {selectedPractitioner ? (
                      <span className="text-slate-900">
                        {selectedPractitioner.full_name ||
                          (selectedPractitioner.first_name && selectedPractitioner.last_name
                            ? `${selectedPractitioner.first_name} ${selectedPractitioner.last_name}`
                            : selectedPractitioner.email)}
                      </span>
                    ) : (
                      <span className="text-slate-500">Search and select a practitioner...</span>
                    )}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 text-slate-400" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-full p-0" align="start" style={{ width: 'var(--radix-popover-trigger-width)' }}>
                  <Command shouldFilter={false}>
                    <CommandInput
                      placeholder="Search practitioners..."
                      value={practitionerSearch}
                      onValueChange={setPractitionerSearch}
                    />
                    <CommandList>
                      {loadingPractitioners ? (
                        <div className="flex items-center justify-center py-4">
                          <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
                        </div>
                      ) : (
                        <>
                          <CommandEmpty>No practitioners found.</CommandEmpty>
                          <CommandGroup>
                            {practitioners.map(p => (
                              <CommandItem
                                key={p.id}
                                value={p.id}
                                onSelect={() => {
                                  setSelectedPractitioner(p);
                                  setPractitionerDropdownOpen(false);
                                }}
                              >
                                <Check
                                  className={`mr-2 h-4 w-4 ${selectedPractitioner?.id === p.id ? 'opacity-100' : 'opacity-0'}`}
                                />
                                <div className="flex flex-col">
                                  <span className="font-medium">
                                    {p.full_name ||
                                      (p.first_name && p.last_name
                                        ? `${p.first_name} ${p.last_name}`
                                        : p.email)}
                                  </span>
                                  {p.email && (
                                    <span className="text-xs text-slate-500">{p.email}</span>
                                  )}
                                </div>
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </>
                      )}
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>

              {selectedPractitioner && (
                <div className="flex items-center gap-3 p-4 rounded-lg bg-teal-50 border border-teal-200">
                  <div className="h-10 w-10 rounded-full bg-brand-teal/20 flex items-center justify-center shrink-0">
                    <User className="h-5 w-5 text-brand-teal" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-teal-900 truncate">
                      {selectedPractitioner.full_name ||
                        (selectedPractitioner.first_name && selectedPractitioner.last_name
                          ? `${selectedPractitioner.first_name} ${selectedPractitioner.last_name}`
                          : selectedPractitioner.email)}
                    </p>
                    {selectedPractitioner.email && (
                      <p className="text-sm text-teal-700 truncate">{selectedPractitioner.email}</p>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0 text-teal-600 hover:text-teal-800 hover:bg-teal-100 shrink-0"
                    onClick={() => setSelectedPractitioner(null)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Step: Program Selection */}
          {STEPS[currentStep]?.id === 'program' && (
            <div className="space-y-4">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-[#60378B]">
                Choose Program
              </h3>
              {loadingPrograms ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  {programs.map((program, index) => {
                    const colors = getColorForIndex(index);
                    const isSelected = selectedProgram?.id === program.id;

                    return (
                      <div
                        key={program.id}
                        className={`cursor-pointer rounded-xl border bg-white overflow-hidden transition-all hover:shadow-md relative ${isSelected
                            ? 'border-brand-teal ring-2 ring-brand-teal/20'
                            : 'border-slate-200 hover:border-slate-300'
                          }`}
                        onClick={() => setSelectedProgram(program)}
                      >
                        {/* Top accent bar */}
                        <div className={`absolute top-0 left-0 right-0 h-1 ${colors.bg}`} />

                        {/* Background gradient */}
                        <div className={`absolute inset-0 bg-gradient-to-br ${colors.accent} to-transparent`} />

                        <div className="relative p-5">
                          <div className="flex items-start gap-4">
                            {/* Program Image or Letter Icon */}
                            {program.image_url ? (
                              <div className="w-12 h-12 rounded-xl overflow-hidden flex-shrink-0">
                                <img
                                  src={program.image_url}
                                  alt={program.title}
                                  className="w-full h-full object-cover"
                                />
                              </div>
                            ) : (
                              <div className={`w-12 h-12 ${colors.bg} rounded-xl flex items-center justify-center flex-shrink-0`}>
                                <span className={`${colors.text} font-semibold text-lg`}>
                                  {getInitials(program.title)}
                                </span>
                              </div>
                            )}

                            <div className="flex-1 min-w-0 pt-1">
                              <h3 className="font-semibold text-slate-900 text-sm leading-tight line-clamp-2">
                                {program.title}
                              </h3>
                              <p className="text-xs text-slate-500 mt-1.5 line-clamp-2 leading-relaxed">
                                {program.subtitle}
                              </p>
                            </div>

                            {isSelected && (
                              <div className="h-5 w-5 rounded-full bg-brand-teal flex items-center justify-center flex-shrink-0">
                                <Check className="h-3 w-3 text-white" />
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Step: Medication Configuration */}
          {STEPS[currentStep]?.id === 'medications' && (
            <div className="space-y-6">
              {/* Available Medications */}
              <div className="space-y-3">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-[#60378B]">
                  Available Medications
                </h3>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <Input
                    value={medicationSearch}
                    onChange={e => setMedicationSearch(e.target.value)}
                    placeholder="Search medications by name or dose..."
                    className="pl-9"
                  />
                </div>
                {loading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
                  </div>
                ) : (
                  <div className="rounded-lg border border-slate-200 overflow-hidden">
                    <ScrollArea className="h-[400px]">
                      <table className="w-full text-sm">
                        <thead>
                          <tr style={{ backgroundColor: 'rgb(245, 247, 247)' }}>
                            <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">Medication</th>
                            <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">Dose</th>
                            <th className="px-4 py-2.5 text-right text-xs font-semibold uppercase tracking-wide text-slate-600">Price</th>
                            <th className="px-4 py-2.5 w-16"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredMedicationRows.length > 0 ? (
                            filteredMedicationRows.map(({ medication, doseInfo }) => {
                              const isPayable = doseInfo.is_payable !== false;
                              const isSelected = selectedMedications.some(
                                m => m.product_id === doseInfo.product_id && m.dose === doseInfo.dose
                              );
                              return (
                                <tr key={`${medication.id}-${doseInfo.dose}`} className={`border-t border-slate-100 ${isPayable ? 'hover:bg-slate-50' : 'bg-amber-50/40'}`}>
                                  <td className="px-4 py-2.5 font-medium text-slate-900">
                                    <div className="flex items-center gap-1.5">
                                      {medication.name}
                                      {!isPayable && (
                                        <TooltipProvider>
                                          <Tooltip>
                                            <TooltipTrigger asChild>
                                              <span className="inline-flex cursor-help">
                                                <AlertTriangle className="h-3.5 w-3.5 text-amber-500 flex-shrink-0" />
                                              </span>
                                            </TooltipTrigger>
                                            <TooltipContent>
                                              <p className="text-xs">Selling price must be greater than base price to enroll this medication.</p>
                                            </TooltipContent>
                                          </Tooltip>
                                        </TooltipProvider>
                                      )}
                                    </div>
                                  </td>
                                  <td className="px-4 py-2.5 text-slate-600">{doseInfo.dose}</td>
                                  <td className="px-4 py-2.5 text-right font-medium text-slate-900">${doseInfo.price}</td>
                                  <td className="px-4 py-2.5 text-right">
                                    {isSelected ? (
                                      <div className="flex items-center gap-1">
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          className="h-7 w-7 p-0 text-brand-teal hover:bg-teal-50"
                                          onClick={() => addMedication(medication, doseInfo)}
                                          title="Add another"
                                        >
                                          <Plus className="h-4 w-4" />
                                        </Button>
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          className="h-7 w-7 p-0 text-slate-400 hover:text-red-500 hover:bg-red-50"
                                          onClick={() => removeMedication(doseInfo.product_id, doseInfo.dose)}
                                        >
                                          <X className="h-4 w-4" />
                                        </Button>
                                      </div>
                                    ) : isPayable ? (
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        className="h-7 w-7 p-0 text-brand-teal hover:text-brand-teal hover:bg-teal-50 text-xs"
                                        onClick={() => addMedication(medication, doseInfo)}
                                      >
                                        Add
                                      </Button>
                                    ) : (
                                      <TooltipProvider>
                                        <Tooltip>
                                          <TooltipTrigger asChild>
                                            <span className="inline-flex">
                                              <Button
                                                size="sm"
                                                variant="ghost"
                                                className="h-7 px-2 text-slate-300 cursor-not-allowed text-xs"
                                                disabled
                                              >
                                                Add
                                              </Button>
                                            </span>
                                          </TooltipTrigger>
                                          <TooltipContent side="left" className="max-w-[220px]">
                                            <p className="text-xs">Selling price must be greater than base price to enroll this medication.</p>
                                          </TooltipContent>
                                        </Tooltip>
                                      </TooltipProvider>
                                    )}
                                  </td>
                                </tr>
                              );
                            })
                          ) : (
                            <tr className="border-t border-slate-100">
                              <td colSpan={4} className="px-4 py-6 text-center text-sm text-slate-500">
                                No medications found for this search.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </ScrollArea>
                  </div>
                )}
              </div>

              {/* Selected Medications */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-[#60378B]">
                    Selected Medications
                  </h3>
                  {selectedMedications.length > 0 && (
                    <span className="text-xs text-slate-500">{selectedMedications.length} item(s)</span>
                  )}
                </div>

                {selectedMedications.length === 0 ? (
                  <div className="rounded-lg border border-dashed border-slate-300 py-8 text-center">
                    <Pill className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                    <p className="text-sm text-slate-500">No medications selected</p>
                    <p className="text-xs text-slate-400 mt-1">Click Add to add medications from the list above</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {selectedMedications.map((med) => (
                      <div key={`${med.product_id}-${med.dose}`} className="flex items-center gap-4 rounded-xl border border-slate-200 p-4 bg-white shadow-sm hover:shadow transition-shadow">
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-teal-50">
                          <Pill className="h-5 w-5 text-brand-teal" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm text-slate-900 truncate">{med.medication}</p>
                          <p className="text-xs text-slate-500">{med.dose}</p>
                          {med.quantity > 1 && (
                            <p className="text-xs text-slate-500 mt-0.5">
                              ${med.price.toFixed(2)} × {med.quantity} = ${(med.price * med.quantity).toFixed(2)}
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-0.5 shrink-0 rounded-lg border border-slate-200 bg-slate-50/50 p-0.5">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-8 w-8 p-0 rounded-md hover:bg-slate-200/80 disabled:opacity-40"
                            onClick={() => updateQuantity(med.product_id, med.dose, med.quantity - 1)}
                            disabled={med.quantity <= 1}
                          >
                            <Minus className="h-4 w-4" />
                          </Button>
                          <span className="text-sm font-semibold text-slate-900 w-8 text-center tabular-nums">
                            {med.quantity}
                          </span>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-8 w-8 p-0 rounded-md hover:bg-slate-200/80 disabled:opacity-40"
                            onClick={() => updateQuantity(med.product_id, med.dose, med.quantity + 1)}
                            disabled={med.quantity >= 99}
                          >
                            <Plus className="h-4 w-4" />
                          </Button>
                        </div>
                        <span className="font-semibold text-sm text-emerald-600 w-20 text-right shrink-0">
                          ${(med.price * med.quantity).toFixed(2)}
                        </span>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-8 w-8 p-0 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 shrink-0"
                          onClick={() => removeMedication(med.product_id, med.dose)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}

                    <div className="flex items-center justify-between pt-3 mt-2 border-t border-slate-200">
                      <span className="text-sm font-medium text-slate-700">Total per cycle</span>
                      <span className="text-lg font-semibold text-slate-900">${totalAmount.toFixed(2)}</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Frequency & Options */}
              <div className="rounded-lg border border-slate-200 p-4 space-y-4" style={{ backgroundColor: 'rgb(250, 251, 251)' }}>
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-wide text-[#60378B]">
                    Subscription Frequency
                  </Label>
                  <Select value={frequency} onValueChange={(v: any) => setFrequency(v)}>
                    <SelectTrigger className="border-slate-200 bg-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                      <SelectItem value="quarterly">Quarterly (Every 3 months)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="autoRenew"
                    checked={autoRenew}
                    onCheckedChange={(checked) => setAutoRenew(checked as boolean)}
                  />
                  <Label htmlFor="autoRenew" className="text-sm text-slate-700">
                    Auto-renew subscription when each cycle ends
                  </Label>
                </div>
              </div>
            </div>
          )}

          {/* Step: Payment & Review */}
          {STEPS[currentStep]?.id === 'payment' && (
            <div className="space-y-5">
              {/* Summary */}
              <div>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-[#60378B] mb-3">
                  Subscription Summary
                </h3>
                <div className="rounded-lg border border-slate-200 p-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Patient</span>
                    <span className="text-slate-900 font-medium">
                      {selectedPatient?.full_name ||
                        `${selectedPatient?.first_name || ''} ${selectedPatient?.last_name || ''}`.trim() ||
                        selectedPatient?.email}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Program</span>
                    <span className="text-slate-900">{selectedProgram?.title || 'Custom'}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Frequency</span>
                    <Badge variant="outline" className="capitalize border-slate-200">
                      {frequency}
                    </Badge>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Carrier</span>
                    <span className="text-slate-900">
                      {carriers.find(c => c.id === selectedCarrierId)?.name || '--'}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Shipment Type</span>
                    <span className="text-slate-900">
                      {shippingOptions.find(option => option.id === selectedShippingOptionId)?.name || '--'}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Auto-renew</span>
                    <span className="text-slate-900">{autoRenew ? 'Yes' : 'No'}</span>
                  </div>
                  <Separator className="bg-slate-200 my-2" />
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Medications</span>
                    <span className="text-slate-900">{selectedMedications.length} item(s)</span>
                  </div>
                  {selectedMedications.map((med) => (
                    <div
                      key={`${med.product_id}-${med.dose}`}
                      className="flex justify-between items-center pl-4 text-sm"
                    >
                      <span className="text-slate-700">
                        {med.medication} ({med.dose})
                        <span className="text-slate-500 ml-1.5">× {med.quantity}</span>
                        {med.quantity > 1 && (
                          <span className="text-slate-400 text-xs ml-1">
                            ${med.price.toFixed(2)} each
                          </span>
                        )}
                      </span>
                      <span className="font-medium text-slate-900">${(med.price * med.quantity).toFixed(2)}</span>
                    </div>
                  ))}
                  <Separator className="bg-slate-200 my-2" />
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Medication Total</span>
                    <span className="text-slate-900">${medicationTotalAmount.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Shipping</span>
                    {isFreeShipment && shippingCostDollars === 0 ? (
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-slate-400 line-through">
                          ${shippingOriginalCostDollars.toFixed(2)}
                        </span>
                        <span className="text-emerald-600 font-medium">$0.00</span>
                      </div>
                    ) : (
                      <span className="text-slate-900">${shippingCostDollars.toFixed(2)}</span>
                    )}
                  </div>
                  <div className="flex justify-between font-medium">
                    <span className="text-slate-900">Total per {frequency} cycle</span>
                    <span className="text-slate-900">${totalAmount.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              <div className="rounded-lg border border-slate-200 p-4 space-y-4">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-[#60378B]">
                  Shipment Details
                </h3>

                {isFreeShipment && patientState && (
                  <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-700">
                    Free shipping available for this patient in {patientState}
                    {freeShipmentType ? ` (${freeShipmentType.replace('_', ' ')})` : ''}
                  </div>
                )}

                <div className="space-y-2">
                  <Label className="text-sm text-slate-700">Carrier</Label>
                  <Select
                    value={selectedCarrierId}
                    onValueChange={handleCarrierChange}
                    disabled={isLoadingCarriers}
                  >
                    <SelectTrigger className="border-slate-200 bg-white">
                      <SelectValue
                        placeholder={isLoadingCarriers ? 'Loading carriers...' : 'Select carrier'}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {carriers.map(carrier => (
                        <SelectItem key={carrier.id} value={carrier.id}>
                          {carrier.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {selectedCarrierId && (
                  <div className="space-y-2">
                    <Label className="text-sm text-slate-700">Shipment Type</Label>
                    <Select
                      value={selectedShippingOptionId}
                      onValueChange={value => handleShippingOptionChange(value)}
                      disabled={isLoadingShippingOptions || shippingOptions.length === 0}
                    >
                      <SelectTrigger className="border-slate-200 bg-white">
                        <SelectValue
                          placeholder={
                            isLoadingShippingOptions
                              ? 'Loading shipment types...'
                              : 'Select shipment type'
                          }
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {shippingOptions.map(option => (
                          <SelectItem key={option.id} value={option.id}>
                            {option.name} -{' '}
                            {isFreeShipment && option.totalCost === 0
                              ? `FREE (was $${option.originalCost.toFixed(2)})`
                              : `$${option.totalCost.toFixed(2)}`}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {shippingError && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{shippingError}</AlertDescription>
                  </Alert>
                )}

                {(!selectedCarrierId || !selectedShippingOptionId) && (
                  <Alert>
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                      Please select both carrier and shipment type before payment.
                    </AlertDescription>
                  </Alert>
                )}
              </div>

              {/* Payment Error Alert */}
              {paymentError && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{paymentError}</AlertDescription>
                </Alert>
              )}

              {/* Payment Form */}
              {stripeAccountId ? (
                <StripePaymentForm
                  connectedAccountId={stripeAccountId}
                  amountCents={Math.round(totalAmount * 100)}
                  onPaymentMethodCreated={handleStripePaymentMethodReceived}
                  onError={(err: string) => handlePaymentError(err)}
                  isProcessing={submitting}
                  disabled={!selectedCarrierId || !selectedShippingOptionId}
                />
              ) : (
                <Alert variant='destructive'>
                  <AlertCircle className='h-4 w-4' />
                  <AlertDescription>
                    Stripe account is not configured for this clinic.
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between border-t border-slate-200 px-6 py-4">
          <div className="flex items-center gap-2">
            {currentStep > 0 && (
              <Button
                variant="outline"
                onClick={prevStep}
                disabled={submitting}
                className="border-slate-200"
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Back
              </Button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
              className="font-medium text-slate-700 hover:bg-transparent hover:text-slate-900"
            >
              Cancel
            </Button>
            {currentStep < STEPS.length - 1 && (
              <Button
                onClick={nextStep}
                disabled={!canProceed()}
                className="h-10 bg-brand-teal hover:bg-brand-teal/90 text-white px-6"
              >
                Next
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            )}
            {currentStep === STEPS.length - 1 && submitting && (
              <Button disabled className="h-10 bg-brand-teal px-6">
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Processing...
              </Button>
            )}
          </div>
        </div>
      </SheetContent>

      <SyringeWarningDialog
        open={showSyringeWarning}
        onOpenChange={setShowSyringeWarning}
        injectableMedications={syringeWarningMedications}
        onGoBack={() => {
          setShowSyringeWarning(false);
        }}
        onAcknowledge={() => {
          setSyringeWarningAcknowledged(true);
          setShowSyringeWarning(false);
          setCurrentStep(prev => prev + 1);
        }}
      />

      <AllergyWarningDialog
        open={showAllergyWarning}
        onOpenChange={setShowAllergyWarning}
        warnings={allergyWarnings}
        patientName={selectedPatient?.full_name}
        patientAllergies={selectedPatient?.medication_allergies}
        onGoBack={() => {
          setShowAllergyWarning(false);
        }}
        onAcknowledge={() => {
          setAllergyWarningsAcknowledged(true);
          setShowAllergyWarning(false);
          // After allergy acknowledgment, run syringe check before advancing
          if (!syringeWarningAcknowledged) {
            const products = selectedMedications.map(med => ({
              product_type: med.product_type || null,
              name: med.medication || '',
            }));
            const result = checkSyringeNeeded(products);
            if (result.needsWarning) {
              setSyringeWarningMedications(result.injectableMedications);
              setShowSyringeWarning(true);
              return;
            }
          }
          setCurrentStep(prev => prev + 1);
        }}
      />
    </Sheet>
  );
};

export default EnrollPatientDialog;
